<template>
  <div
    :class="
      twMerge(
        'tw:flex tw:flex-col',
        !menu &&
          'tw:has-[[role=button]:focus-visible]:outline-2 tw:has-[[role=button]:focus-visible]:outline-offset-2 tw:has-[[role=button]:focus-visible]:outline-primary',
        !menu && 'tw:bg-white',
        bordered && 'tw:border tw:border-solid tw:border-denim-300',
        bordered && dense && 'tw:rounded',
        bordered && !dense && 'tw:rounded-md',
        fitContainer && expanded && 'tw:flex-1',
        (!expanded || fitContainer) && 'tw:overflow-hidden',
        panelClass
      )
    "
    data-cy="base-expansion-panel"
    data-testid="base-expansion-panel"
  >
    <div
      :aria-controls="customTrigger ? undefined : contentId"
      :aria-disabled="!customTrigger && disabled ? 'true' : undefined"
      :aria-expanded="customTrigger ? undefined : expanded"
      :class="
        twMerge(
          'tw:flex tw:items-center tw:focus-visible:outline-none',
          bordered && dense && 'tw:rounded-t',
          bordered && !dense && 'tw:rounded-t-md',
          !menu && 'tw:bg-white',
          !menu && !disabled && 'tw:hover:bg-denim-100',
          menu && 'tw:flex-row-reverse tw:hover:bg-white/15',
          menu && active && 'tw:bg-white/20',
          stickyHeader && expanded && 'tw:sticky tw:top-0 tw:z-50',
          !disabled && 'tw:cursor-pointer',
          headerClass
        )
      "
      data-cy="base-expansion-panel-header"
      data-testid="base-expansion-panel-header"
      :role="customTrigger ? undefined : 'button'"
      :style="headerStyle"
      :tabindex="customTrigger ? undefined : disabled ? -1 : 0"
      @click="onHeaderClick"
      @keydown.enter="onHeaderActivate"
      @keydown.space="onSpaceKeydown"
      @keyup.space="onHeaderActivate"
      @mouseout="onMouseOut"
      @mouseover="onMouseOver"
    >
      <slot
        :close="closePanelFromSlot"
        :content-id="contentId"
        :disabled="disabled"
        :expanded="expanded"
        :mouseover="mouseover"
        name="header"
        :open="openPanelFromSlot"
        :toggle="togglePanelFromSlot"
      >
        <div
          class="tw:flex tw:flex-1 tw:flex-col tw:gap-1"
          :class="{
            'tw:px-1.5 tw:py-1': !noPadding && (dense || menu),
            'tw:px-4 tw:py-3': !noPadding && !dense && !menu,
          }"
        >
          <slot
            :expanded="expanded"
            name="label"
          >
            <div :class="{ 'tw:font-bold': boldLabel }">{{ label }}</div>
          </slot>
          <div
            v-if="caption"
            class="tw:text-xs"
          >
            {{ caption }}
          </div>
          <div
            v-if="errorMessage"
            class="tw:text-xs tw:font-bold tw:text-red-700"
            data-testid="base-expansion-panel-error"
            role="alert"
          >
            {{ errorMessage }}
          </div>
        </div>
      </slot>
      <component
        :is="customTrigger ? 'button' : 'div'"
        v-if="!disabled"
        :aria-controls="customTrigger ? contentId : undefined"
        :aria-expanded="customTrigger ? expanded : undefined"
        :aria-label="customTrigger ? label || undefined : undefined"
        :class="
          twMerge(
            customTrigger &&
              'tw:border-0 tw:bg-transparent tw:text-inherit tw:focus-visible:opacity-100 tw:cursor-pointer',
            hoverToggle && !mouseover && 'tw:opacity-0',
            menu && 'tw:hover:bg-white/15',
            !noPadding && menu && 'tw:px-0.5 tw:py-2',
            !noPadding && dense && !menu && 'tw:px-1.5 tw:py-1',
            !noPadding && !dense && !menu && 'tw:px-4 tw:py-3',
            componentClass
          )
        "
        data-testid="base-expansion-panel-trigger"
        :style="headerIconContainerStyle"
        :type="customTrigger ? 'button' : undefined"
        @click="onTriggerClick"
      >
        <div
          class="tw:transition-all tw:duration-300"
          :class="{
            'tw:rotate-180': expanded && !menu,
            'tw:rotate-90': expanded && menu,
          }"
        >
          <BaseIcon
            :class="{ 'tw:text-xl': !dense && !menu }"
            :icon="menu ? 'angle-right' : 'angle-down'"
            :style="headerIconStyle"
          />
        </div>
      </component>
    </div>
    <div
      :id="contentId"
      ref="container"
      class="base-expansion-panel-container tw:flex tw:flex-col"
      :class="{
        'tw:h-0': !expanded,
        'tw:flex-1': !inTransition && fitContainer && expanded,
        'tw:overflow-hidden':
          !expanded || inTransition || (fitContainer && !scrollContainer),
        'tw:overflow-auto': scrollContainer && expanded,
      }"
      data-cy="base-expansion-panel-container"
      data-testid="base-expansion-panel-container"
      :inert="isHidden ? true : undefined"
      :style="containerStyle"
    >
      <div
        ref="content"
        class="tw:flex tw:flex-col"
        :class="{
          'tw:p-1':
            !noPadding && dense && (!fitContainer || scrollContainer) && !menu,
          'tw:px-4 tw:py-3':
            !noPadding && !dense && (!fitContainer || scrollContainer) && !menu,
          'tw:flex-1': fitContainer && !scrollContainer,
          'tw:overflow-hidden': fitContainer && !scrollContainer,
        }"
        data-cy="base-expansion-panel-content"
        data-testid="base-expansion-panel-content"
      >
        <slot
          :close="closePanelFromSlot"
          :expanded="expanded && !inTransition"
          :in-transition="inTransition"
          :toggle="togglePanelFromSlot"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { useId } from "vue";

import type { Model, Props } from "shared/composables/useExpansionPanel";
import useExpansionPanel from "shared/composables/useExpansionPanel";

import BaseIcon from "../BaseIcon";

const modelValue = defineModel<Model>({ default: false });

const props = withDefaults(defineProps<Props>(), {
  label: "",
  caption: "",
  errorMessage: "",
  group: "",
  duration: 300,
});

const contentId = `base-expansion-panel-content-${useId()}`;
let didToggleFromSlot = false;

const {
  container,
  content,
  containerStyle,
  mouseover,
  expanded,
  inTransition,
  isHidden,
  togglePanel,
  openPanel,
  closePanel,
  onMouseOver,
  onMouseOut,
} = useExpansionPanel(modelValue, props);

function isInteractiveTarget(event: Event): boolean {
  if (event.target instanceof Element) {
    const interactiveElement = event.target.closest(
      "a, button, input, select, textarea, [role='button'], [role='link']"
    );

    if (interactiveElement && interactiveElement !== event.currentTarget) {
      return true;
    }
  }

  return false;
}

function onHeaderClick(event: MouseEvent): void {
  if (didToggleFromSlot) {
    didToggleFromSlot = false;

    return;
  }

  if (isInteractiveTarget(event)) {
    return;
  }

  togglePanel();
}

function onTriggerClick(event: MouseEvent): void {
  if (!props.customTrigger) return;

  event.stopPropagation();
  togglePanel();
}

// Slot consumers may bind toggle/open/close to elements inside the header.
// When the slot handler fires, the click also bubbles to the header's own
// handler. Setting `didToggleFromSlot` lets the bubbled handler skip its
// action; the microtask resets the flag if the bubble was suppressed (e.g.
// `.stop`) or the slot invoked the action outside of an event.
function guardSlotAction(action: () => void) {
  return (event?: Event): void => {
    event?.stopPropagation();
    didToggleFromSlot = true;
    action();

    queueMicrotask(() => {
      didToggleFromSlot = false;
    });
  };
}

const togglePanelFromSlot = guardSlotAction(togglePanel);
const openPanelFromSlot = guardSlotAction(openPanel);
const closePanelFromSlot = guardSlotAction(closePanel);

function onHeaderActivate(event: KeyboardEvent): void {
  if (props.customTrigger) return;

  if (event.repeat) {
    return;
  }

  if (didToggleFromSlot) {
    didToggleFromSlot = false;

    return;
  }

  if (isInteractiveTarget(event)) {
    return;
  }

  event.preventDefault();
  togglePanel();
}

function onSpaceKeydown(event: KeyboardEvent): void {
  if (props.customTrigger) return;

  if (isInteractiveTarget(event)) {
    return;
  }

  event.preventDefault();
}
</script>
